/**
 * Pure validation + payload-normalisation helpers for the Student Billing
 * Information section. Extracted so both the Create and Edit forms use the
 * exact same rules, and so we can unit-test currency handling without a
 * DOM.
 *
 * The single most important rule: `monthly_amount` is stored as a RAW
 * STRING in Formik / component state. `$` is a visual InputAdornment only.
 * We convert to Number at two boundaries: validation and payload
 * normalisation. Validating the display value (e.g. "$5.00") would
 * always fail, so we never do that.
 */

export type BillingTypeV2 = "monthly" | "class_wise";

export interface BillingValuesInput {
  billing_type_v2: BillingTypeV2 | "" | null | undefined;
  // Accept string (typed input) or number (legacy) or null.
  monthly_amount: string | number | null | undefined;
  billing_effective_from?: string | null;
  tax_applicable?: boolean;
}

/**
 * Returns the validation error for `monthly_amount` — or undefined when
 * valid or not applicable (Class-Wise has no monthly amount).
 *
 *  - Class-Wise: never requires an amount → undefined.
 *  - Monthly: requires a finite number > 0. Empty string, null,
 *    non-numeric strings and 0 all fail.
 */
export function validateMonthlyAmount(
  type: BillingTypeV2 | "" | null | undefined,
  raw: string | number | null | undefined
): string | undefined {
  if (type !== "monthly") return undefined;
  if (raw === null || raw === undefined || raw === "") {
    return "Monthly amount is required and must be greater than 0.";
  }
  const amount = Number(raw);
  if (!Number.isFinite(amount) || amount <= 0) {
    return "Monthly amount is required and must be greater than 0.";
  }
  return undefined;
}

/**
 * Normalises a billing_type_v2 + monthly_amount pair into what the API
 * expects: a numeric amount for Monthly, `null` for Class-Wise. Never
 * returns the display value (no "$", no formatted string).
 */
export function normalizeMonthlyAmountForPayload(
  type: BillingTypeV2 | "" | null | undefined,
  raw: string | number | null | undefined
): number | null {
  if (type !== "monthly") return null;
  if (raw === null || raw === undefined || raw === "") return NaN;
  const n = Number(raw);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * Accept helper for the onChange of the currency <input>. Returns the
 * next stored value ("" or the raw numeric string) or `null` to signal
 * "reject this keystroke and keep the previous value".
 *
 * Rules:
 *  - "" resets the field.
 *  - Digits with at most one dot and at most two decimals allowed.
 *  - Anything else (letters, "5.5.5", "5.123") is rejected.
 *
 * Mid-typing states such as "5." are ALLOWED so the user can keep typing.
 */
export function acceptCurrencyKeystroke(raw: string): string | null {
  if (raw === "") return "";
  return /^\d*(\.\d{0,2})?$/.test(raw) ? raw : null;
}
